import { useEffect, useState } from 'react';

/** Id of the synthetic select-all row; cannot collide with a real option. */
export const ALL_OPTION_ID = '__all__';

/**
 * Staged multi-select over a KeyDS OptionsList.
 *
 * Two menus in User Management share this behaviour: the list page's filter menus
 * (PD-4063) and the Create User wizard's Accounts field (PD-4064). Both check
 * boxes into a draft that nothing outside sees until APPLY, both offer a
 * select-all row above the options, and both have to forget an abandoned draft
 * when they reopen.
 *
 * Selections are held as strings throughout, since that is what OptionsList
 * reports and what survives a URL round trip. Numeric option values (account ids)
 * are stringified on the way in.
 *
 * @param {object} params
 * @param {boolean} params.isOpen        reopening resets the draft to `selected`
 * @param {Array}   params.options       `{ label, value }` pairs, already narrowed
 * @param {Array}   params.selected      what is currently applied
 * @param {boolean} params.allOption     prepend a select-all row
 * @param {string}  params.allOptionLabel text for that row
 */
const useStagedSelection = ({
  allOption = false,
  allOptionLabel = 'All',
  isOpen,
  options = [],
  selected = [],
}) => {
  const [draft, setDraft] = useState(selected.map(String));

  // Keyed on the contents rather than the array, because a caller that builds
  // `selected` inline hands over a new array on every render — which would reset
  // the draft on each keystroke and lose everything the user had checked.
  const appliedKey = selected.map(String).join(' ');

  // Reopening shows what is actually applied, discarding any abandoned draft.
  useEffect(() => {
    if (isOpen) setDraft(appliedKey === '' ? [] : appliedKey.split(' '));
  }, [isOpen, appliedKey]);

  const visibleIds = options.map((option) => String(option.value));
  const allChecked =
    visibleIds.length > 0 && visibleIds.every((id) => draft.includes(id));

  const items = [
    ...(allOption ? [{ id: ALL_OPTION_ID, label: allOptionLabel }] : []),
    ...options.map((option) => ({
      id: String(option.value),
      label: option.label,
    })),
  ];

  const selectedIds =
    allOption && allChecked ? [ALL_OPTION_ID, ...draft] : draft;

  // The select-all row is a toggle rather than a value of its own: ticking it
  // takes every visible option, unticking it takes none.
  const handleChange = (ids) => {
    if (!allOption) {
      setDraft(ids);
      return;
    }

    const wantsAll = ids.includes(ALL_OPTION_ID);

    if (wantsAll && !allChecked) {
      setDraft(visibleIds);
      return;
    }

    if (!wantsAll && allChecked) {
      setDraft([]);
      return;
    }

    setDraft(ids.filter((id) => id !== ALL_OPTION_ID));
  };

  return { draft, handleChange, items, selectedIds, setDraft };
};

export default useStagedSelection;
